import React, { useState } from "react";
import classes from "./Signup.module.css";
import logo from "../../img/LoginImg/Logo.png";
import google from "../../img/LoginImg/google.png";
import x from "../../img/LoginImg/x.svg";

import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithPopup,
  GoogleAuthProvider,
  deleteUser,
  signOut,
} from "firebase/auth";
import { auth, db } from "../../firebase/firebase"; // ✅ הוספה
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore"; // ✅ הוספה
import WelcomeModal from "../WelcomeModal/WelcomeModal";

// Map common Firebase auth errors to Hebrew messages
const authErrorToHebrew = (code) => {
  switch (code) {
    case "auth/email-already-in-use":
      return "כתובת המייל כבר רשומה במערכת. נסה להתחבר.";
    case "auth/invalid-email":
      return "כתובת מייל לא תקינה";
    case "auth/weak-password":
      return "הסיסמה חלשה מדי (לפחות 6 תווים)";
    case "auth/network-request-failed":
      return "בעיית רשת, נסה שוב";
    case "auth/operation-not-allowed":
      return "הרשמה במייל אינה מאופשרת כרגע";
    case "auth/too-many-requests":
      return "יותר מדי ניסיונות. נסה שוב מאוחר יותר";
    default:
      return "שגיאה בהרשמה, נסה שוב";
  }
};

const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

function Signup({ onClose, onSwitchToLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeRequiresVerification, setWelcomeRequiresVerification] = useState(false);
  const [welcomeEmail, setWelcomeEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleSignup = async () => {
    if (submitting) return;
    setErrorMsg("");

    // ---- Client-side validation ----
    const emailTrim = email.trim();
    if (!emailTrim || !password || !confirmPassword) {
      setErrorMsg("יש למלא את כל השדות");
      return;
    }
    if (!isValidEmail(emailTrim)) {
      setErrorMsg("כתובת מייל לא תקינה");
      return;
    }
    if (password.length < 6) {
      setErrorMsg("הסיסמה חייבת להכיל לפחות 6 תווים");
      return;
    }
    if (password !== confirmPassword) {
      setErrorMsg("הסיסמאות אינן תואמות");
      return;
    }

    setSubmitting(true);
    let createdUser = null;
    try {
      const result = await createUserWithEmailAndPassword(
        auth,
        emailTrim,
        password
      );
      createdUser = result.user;

      const userRef = doc(db, "users", createdUser.uid);

      // For a fresh email/password signup the doc never exists, but
      // keep the check defensively (no-op for new accounts).
      const existingUser = await getDoc(userRef);
      console.log("📋 User UID:", createdUser.uid);
      console.log("📋 Existing user in Firestore:", existingUser.exists());

      if (!existingUser.exists()) {
        console.log("➕ Creating new user document with 2 free credits");
        await setDoc(userRef, {
          email: createdUser.email,
          currentPlan: "free",
          planStatus: "none",
          credits: {
            remaining: 2,
            lastUpdatedAt: serverTimestamp(),
          },
          entitlements: {
            canDownloadFull: false,
          },
          stats: {
            converted: 0,
            downloaded: 0,
            lastUpdated: serverTimestamp(),
          },
          auditVersion: 1,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } else {
        console.log("✅ User already exists - NOT overwriting anything");
      }

      // Best-effort: don't fail the whole signup if verification email fails
      try {
        await sendEmailVerification(createdUser);
        console.log("Verification email sent");
      } catch (verErr) {
        console.warn("sendEmailVerification failed:", verErr);
      }

      setWelcomeEmail(createdUser.email || emailTrim);
      setWelcomeRequiresVerification(true);
      setShowWelcome(true);
      setTimeout(() => {
        signOut(auth).catch((signOutErr) => {
          console.error("signOut after signup verification failed:", signOutErr);
        });
      }, 0);
    } catch (error) {
      console.error("Signup failed:", error);

      // If we already created an Auth user but Firestore write failed,
      // roll back the Auth user so the email isn't permanently locked.
      if (createdUser && error?.code !== "auth/email-already-in-use") {
        try {
          await deleteUser(createdUser);
          console.warn("Rolled back auth user after signup failure");
        } catch (rollbackErr) {
          console.error("Rollback deleteUser failed:", rollbackErr);
        }
      }

      setErrorMsg(authErrorToHebrew(error?.code));
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleSignup = async () => {
    setErrorMsg("");
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // 🔸 בדיקה אם המשתמש כבר קיים
      const userRef = doc(db, "users", user.uid);
      const existingUser = await getDoc(userRef);

      console.log("📋 [Google] User UID:", user.uid);
      console.log("📋 [Google] Existing user in Firestore:", existingUser.exists());
      if (existingUser.exists()) {
        console.log("📋 [Google] Existing user data:", existingUser.data());
      }

      if (!existingUser.exists()) {
        // משתמש חדש - יצירת דוקומנט עם 2 קרדיטים חינם
        console.log("➕ [Google] Creating new user document with 2 free credits");
        await setDoc(userRef, {
          email: user.email,
          currentPlan: "free",
          planStatus: "none",
          credits: {
            remaining: 2,  // 2 ניסיונות חינם
            lastUpdatedAt: serverTimestamp(),
          },
          entitlements: {
            canDownloadFull: false,
          },
          stats: {
            converted: 0,
            downloaded: 0,
            lastUpdated: serverTimestamp(),
          },
          auditVersion: 1,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        console.log("Google signup:", user);
        setWelcomeRequiresVerification(false);
        setWelcomeEmail(user.email || "");
        setShowWelcome(true);
        return;
      } else {
        console.log("✅ [Google] User already exists - NOT overwriting anything");
      }
      // אם המשתמש קיים - לא דורסים כלום, הקרדיטים נשמרים

      onClose();
    } catch (error) {
      console.error("Google signup failed:", error.message);
      if (error?.code !== "auth/popup-closed-by-user" && error?.code !== "auth/cancelled-popup-request") {
        setErrorMsg("ההרשמה עם גוגל נכשלה. נסה שוב.");
      }
    }
  };

  return (
    <>
    <WelcomeModal
      isOpen={showWelcome}
      onClose={onClose}
      requiresVerification={welcomeRequiresVerification}
      email={welcomeEmail}
    />
    <div className={classes.modalContainer}>
      <div className={classes.modalContent}>
        <button onClick={onClose} className={classes.closeButton}>
          <img src={x} alt="סגור" />
        </button>
        <img src={logo} className={classes.logo} alt="Logo" />
        <h2 className={classes.modalTitle}>הרשמה</h2>

        <button className={classes.googleLogin} onClick={handleGoogleSignup}>
          הצטרפות מהירה עם גוגל
          <img src={google} className={classes.googleIcon} alt="Google Icon" />
        </button>

        <p className={classes.separator}>או הירשם באמצעות מייל וסיסמא</p>

        <input
          type="email"
          placeholder="אימייל"
          className={classes.inputField}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <input
          type="password"
          placeholder="סיסמא"
          className={classes.inputField}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
        />

        <input
          type="password"
          placeholder="חזור על הסיסמא"
          className={classes.inputField}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
        />

        {errorMsg && (
          <div
            style={{
              background: "#FEE2E2",
              border: "1px solid #EF4444",
              borderRadius: 12,
              padding: "10px 14px",
              margin: "0 auto 12px",
              width: "80%",
              fontSize: 14,
              color: "#991B1B",
              direction: "rtl",
              textAlign: "right",
            }}
          >
            {errorMsg}
          </div>
        )}

        <button
          className={classes.switchToLoginButton}
          onClick={onSwitchToLogin}
        >
          כבר יש לך חשבון? התחבר
        </button>

        <button
          className={classes.submitButton}
          onClick={handleSignup}
          disabled={submitting}
        >
          {submitting ? "נרשם..." : "הירשם"}
        </button>
      </div>
    </div>
    </>
  );
}

export default Signup;
